/**
 * Brand display values resolved from company settings, with sensible platform
 * defaults applied. This is a pure view-model — it carries no loading/error
 * state. Inside the app, read it from `useTenantData().branding`; on pages
 * that render outside `TenantDataProvider` (e.g. admin), resolve it directly
 * from a fetched `CompanySettings` via {@link resolveBrandSettings}.
 */
export interface BrandSettings {
  logoPath: string | null;
  /**
   * True when the tenant has uploaded their own logo, false when we are
   * displaying the default Routemize logo. Useful for UI that wants to
   * distinguish "company branding" vs "platform branding".
   */
  hasCustomLogo: boolean;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  businessName: string;
  businessPhone: string | null;
  businessWebsite: string | null;
}

export const DEFAULT_PRIMARY_COLOR = '#6B21A8'; // Purple
export const DEFAULT_SECONDARY_COLOR = '#3B82F6'; // Blue
export const DEFAULT_TERTIARY_COLOR = '#f1f5f9'; // Slate-100
export const DEFAULT_LOGO = 'https://ids.routemize.com/images/logo/routemize-logo-light.png';
export const DEFAULT_BUSINESS_NAME = 'Your Business';

/**
 * True when a stored logo path is actually renderable as an image source.
 * New tenants can be seeded with a bare filename (e.g. "logo-7641733172.jpeg")
 * that isn't a usable URL — treat those as "no logo" so the UI falls back to
 * the platform default instead of showing a broken image or junk input value.
 */
export function isUsableLogoUrl(path: string | null | undefined): boolean {
  if (!path) return false;
  const trimmed = path.trim();
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('/')
  );
}

/** Subset of company-settings fields needed to derive brand display values. */
export interface BrandSource {
  logoPath?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  tertiaryColor?: string | null;
  businessName?: string | null;
  businessWebsite?: string | null;
  phone?: string | null;
}

/**
 * Map raw company-settings fields to display-ready brand values, falling back
 * to platform defaults for anything the tenant hasn't set.
 */
export function resolveBrandSettings(source: BrandSource | null | undefined): BrandSettings {
  const logo = isUsableLogoUrl(source?.logoPath) ? source!.logoPath! : null;
  return {
    logoPath: logo || DEFAULT_LOGO,
    hasCustomLogo: !!logo,
    primaryColor: source?.primaryColor || DEFAULT_PRIMARY_COLOR,
    secondaryColor: source?.secondaryColor || DEFAULT_SECONDARY_COLOR,
    tertiaryColor: source?.tertiaryColor || DEFAULT_TERTIARY_COLOR,
    businessName: source?.businessName || DEFAULT_BUSINESS_NAME,
    businessPhone: source?.phone || null,
    businessWebsite: source?.businessWebsite || null,
  };
}
